import { useState, useEffect } from "react";
import { API } from "../../App";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Tag, ToggleLeft, ToggleRight, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import SegmentMultiSelect from "../../components/SegmentMultiSelect";

export default function AdminDiscount() {
  const [config, setConfig] = useState({ enabled: false, type: "percentage", value: 0, applicable_to: "both", label: "App Only Discount", allowed_segment_ids: [], target: "all" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchConfig(); }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${API}/settings/discount`, { credentials: "include" });
      if (res.ok) setConfig(await res.json());
    } catch {}
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/settings/discount`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(config),
      });
      if (res.ok) toast.success("Discount config saved");
      else { const err = await res.json(); toast.error(err.detail || "Failed to save"); }
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  if (loading) return <AdminLayout><div className="p-6"><div className="h-40 bg-gray-100 animate-pulse rounded-xl" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="animate-fade-in max-w-lg" data-testid="admin-discount">
        <div className="flex items-center gap-3 mb-6">
          <Tag className="w-6 h-6 text-green-600" />
          <h1 className="text-xl font-bold text-gray-900">Discount Configuration</h1>
        </div>

        <div className="bg-white rounded-xl border p-5 space-y-5">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-900">Enable Discount</p>
              <p className="text-xs text-gray-500">When enabled, discount applies at checkout</p>
            </div>
            <button
              onClick={() => setConfig({ ...config, enabled: !config.enabled })}
              className={`p-1 rounded-lg transition-colors ${config.enabled ? "text-green-600" : "text-gray-400"}`}
              data-testid="discount-toggle"
            >
              {config.enabled ? <ToggleRight size={36} /> : <ToggleLeft size={36} />}
            </button>
          </div>

          {/* Label */}
          <div>
            <label className="text-xs text-gray-600 font-medium mb-1 block">Discount Label</label>
            <Input
              value={config.label}
              onChange={(e) => setConfig({ ...config, label: e.target.value })}
              placeholder="App Only Discount"
              className="text-sm"
              data-testid="discount-label"
            />
          </div>

          {/* Discount Type */}
          <div>
            <label className="text-xs text-gray-600 font-medium mb-2 block">Discount Type</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                className={`p-3 rounded-xl border-2 text-center transition-all ${config.type === "percentage" ? "border-green-500 bg-green-50 text-green-700" : "border-gray-200 text-gray-500"}`}
                onClick={() => setConfig({ ...config, type: "percentage" })}
                data-testid="type-percentage"
              >
                <span className="text-lg font-bold">%</span>
                <p className="text-xs mt-0.5">Percentage</p>
              </button>
              <button
                className={`p-3 rounded-xl border-2 text-center transition-all ${config.type === "flat" ? "border-green-500 bg-green-50 text-green-700" : "border-gray-200 text-gray-500"}`}
                onClick={() => setConfig({ ...config, type: "flat" })}
                data-testid="type-flat"
              >
                <span className="text-lg font-bold">&#8377;</span>
                <p className="text-xs mt-0.5">Flat Amount</p>
              </button>
            </div>
          </div>

          {/* Value */}
          <div>
            <label className="text-xs text-gray-600 font-medium mb-1 block">
              {config.type === "percentage" ? "Percentage Value (%)" : "Flat Amount (₹)"}
            </label>
            <Input
              type="number"
              min="0"
              max={config.type === "percentage" ? 100 : undefined}
              value={config.value}
              onChange={(e) => setConfig({ ...config, value: parseFloat(e.target.value) || 0 })}
              className="text-sm"
              data-testid="discount-value"
            />
          </div>

          {/* Applicability */}
          <div>
            <label className="text-xs text-gray-600 font-medium mb-2 block">Applies To</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { key: "both", label: "Both" },
                { key: "wallet", label: "Wallet Only" },
                { key: "gateway", label: "Online Only" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  className={`p-2.5 rounded-xl border-2 text-center text-xs font-semibold transition-all ${config.applicable_to === opt.key ? "border-green-500 bg-green-50 text-green-700" : "border-gray-200 text-gray-500"}`}
                  onClick={() => setConfig({ ...config, applicable_to: opt.key })}
                  data-testid={`applicable-${opt.key}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Customer Segments restriction */}
          <div>
            <label className="text-xs text-gray-600 font-medium mb-2 block">Who gets this discount?</label>
            <div className="grid grid-cols-3 gap-2 mb-2">
              {[
                { key: "all", label: "All Customers" },
                { key: "segments", label: "Specific Segments" },
                { key: "no_segment", label: "No Segment" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  className={`p-2.5 rounded-xl border-2 text-center text-xs font-semibold transition-all ${config.target === opt.key ? "border-green-500 bg-green-50 text-green-700" : "border-gray-200 text-gray-500"}`}
                  onClick={() => setConfig({ ...config, target: opt.key, allowed_segment_ids: opt.key === "segments" ? config.allowed_segment_ids : [] })}
                  data-testid={`discount-target-${opt.key}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-gray-500 leading-snug">
              <span className="font-semibold text-gray-600">All Customers</span>: applies to every paying customer. {" "}
              <span className="font-semibold text-gray-600">Specific Segments</span>: only customers in the chosen segments. {" "}
              <span className="font-semibold text-gray-600">No Segment</span>: only customers who have no segment assigned (general public users).
            </p>
          </div>

          {config.target === "segments" && (
            <SegmentMultiSelect
              value={config.allowed_segment_ids || []}
              onChange={(ids) => setConfig({ ...config, allowed_segment_ids: ids })}
              label="Select Customer Segments"
              helperText="Only customers in the selected segments receive the discount at checkout."
              testIdPrefix="discount-form-segments"
            />
          )}

          {/* Preview */}
          {config.enabled && (
            <div className="p-3 bg-gray-50 rounded-lg border">
              <p className="text-xs text-gray-500 mb-1">Preview at checkout:</p>
              <p className="text-sm font-semibold text-green-700">
                {config.label}: {config.type === "percentage" ? `${config.value}% off` : `₹${config.value} off`}
                {config.applicable_to !== "both" && <span className="text-gray-500 font-normal"> (on {config.applicable_to === "wallet" ? "wallet" : "online"} payments)</span>}
                {(config.target === "segments" && (config.allowed_segment_ids?.length || 0) > 0) && <span className="text-gray-500 font-normal"> · restricted to {config.allowed_segment_ids.length} segment(s)</span>}
                {config.target === "no_segment" && <span className="text-gray-500 font-normal"> · only no-segment customers</span>}
              </p>
            </div>
          )}

          {/* Save Button */}
          <Button onClick={handleSave} disabled={saving} className="w-full bg-green-600 hover:bg-green-700 text-white" data-testid="save-discount-btn">
            {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
            Save Configuration
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
}
